import type { Container } from 'pixi.js';
import { getLocalized } from '../libs/layouts/initPopulate';

export const SECONDS_IN_MINUTE = 60;
export const SECONDS_IN_HOUR = 60 * SECONDS_IN_MINUTE;
export const MS_IN_HOUR = 3600000;
export const MS_IN_DAY = 86400000;
export const MS_IN_MINUTE = 60000;

export enum CountDownFormat {
  // days
  D = 'd',
  // days, hours
  DH = 'd:h',
  // days, hours, minutes
  DHM = 'd:h:m',
  // days, hours, seconds
  DHMS = 'd:h:m:s',
  // hours, minutes
  HM = 'h:m',
  // hours, minutes, seconds
  HMS = 'h:m:s',
  // minutes, seconds
  MS = 'm:s',
  // seconds
  S = 's',
}

interface TimeUnit {
  amount(ms: number): number;
  amountUncapped(ms: number): number;
  intoLabel(amount: string): string;
  padding: boolean;
}

const timeUnits: Record<string, TimeUnit> = {
  d: {
    amount: (ms: number): number => Math.floor(ms / MS_IN_DAY),
    amountUncapped: (ms: number): number => Math.floor(ms / MS_IN_DAY),
    padding: false,
    intoLabel: (amount: string): string => getLocalized('<amount>d').replace('<amount>', amount),
  },
  h: {
    amount: (ms: number): number => Math.floor((ms % MS_IN_DAY) / MS_IN_HOUR),
    amountUncapped: (ms: number): number => Math.floor(ms / MS_IN_HOUR),
    padding: true,
    intoLabel: (amount: string): string => getLocalized('<amount>h').replace('<amount>', amount),
  },
  m: {
    amount: (ms: number): number => Math.floor((ms % MS_IN_HOUR) / MS_IN_MINUTE),
    amountUncapped: (ms: number): number => Math.floor(ms / MS_IN_MINUTE),
    padding: true,
    intoLabel: (amount: string): string => getLocalized('<amount>m').replace('<amount>', amount),
  },
  s: {
    amount: (ms: number): number => Math.floor((ms % MS_IN_MINUTE) / 1000),
    amountUncapped: (ms: number): number => Math.floor(ms / 1000),
    padding: true,
    intoLabel: (amount: string): string => getLocalized('<amount>s').replace('<amount>', amount),
  },
};

// turn countdown into x position labeled text:
// in case of 2 displayed position it may result in Xd Xh or Xm Xs depending on passed time
export function formatIntoUnitCountdown(countdownMs: number, positionAmount: number, format: CountDownFormat): string {
  const allowedUnits = format.split(':');

  const units = Object.entries(timeUnits)
    .filter(([key]) => allowedUnits.includes(key))
    .map(([_, unit]) => unit);

  while (units[0].amount(countdownMs) === 0 && units.length > 1) {
    units.shift();
  }

  return units
    .slice(0, positionAmount)
    .map((unit, index) => {
      let amount = unit.amount(countdownMs);

      if (
        amount === 0 &&
        index === units.length - 1 &&
        countdownMs > 0 &&
        units.every((u) => u.amount(countdownMs) === 0)
      ) {
        amount += 1;
      }

      return unit.intoLabel(`${amount}`);
    })
    .join(' ');
}

export function formatFixedCountdown(countdownMs: number, format: CountDownFormat = CountDownFormat.HMS): string {
  return format
    .split(':')
    .map((key) => `${timeUnits[key]?.amount(countdownMs) ?? ''}`.padStart(2, '0'))
    .join(':');
}

export function getMsUntil(timestamp: number): number {
  return Math.max(timestamp - Date.now(), 0);
}

export function daysSinceEpoch(): number {
  const now = new Date();
  const epoch = new Date(1970, 0, 1); // Epoch date
  const diffTime = now.getTime() - epoch.getTime();
  return Math.floor(diffTime / (1000 * 60 * 60 * 24)); // Convert to days
}

export function attachIntervalToDisplayObject(object: Container, callback: () => void, interval = 1000): () => void {
  const action = (): void => {
    if (object.destroyed) return;
    callback();
  };
  action();
  const intervalId = setInterval(action, interval);
  return () => {
    clearInterval(intervalId);
  };
}
